import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, Upload, Sparkles, Edit2, Trash2, Copy, X, Save } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const VOICE_COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'];

const EMPTY_FORM = {
  name: '',
  description: '',
  domain: '',
  category: 'custom',
  voices: [
    { name: 'Voice 1', mandate: '', model: 'google/gemini-2.5-flash', color: VOICE_COLORS[0] },
    { name: 'Voice 2', mandate: '', model: 'google/gemini-2.5-flash', color: VOICE_COLORS[1] },
  ],
  phases: [],
  complexity_tier: 'standard',
};

export default function PlaybookGallery() {
  const { user } = useAuth() || {};
  const [activeTab, setActiveTab] = useState('browse');
  const [playbooks, setPlaybooks] = useState([]);
  const [myPlaybooks, setMyPlaybooks] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [myLoading, setMyLoading] = useState(false);

  // CRUD state
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [forking, setForking] = useState(null);

  // Load browse playbooks
  useEffect(() => {
    API.get('/api/playbooks')
      .then(r => setPlaybooks(r.data.playbooks || []))
      .finally(() => setLoading(false));
  }, []);

  // Load user playbooks when tab switches
  const loadMyPlaybooks = useCallback(() => {
    if (!user) return;
    setMyLoading(true);
    API.get('/api/user/playbooks')
      .then(r => setMyPlaybooks(r.data.playbooks || []))
      .catch(() => setMyPlaybooks([]))
      .finally(() => setMyLoading(false));
  }, [user]);

  useEffect(() => {
    if (activeTab === 'my' && user) loadMyPlaybooks();
  }, [activeTab, user, loadMyPlaybooks]);

  const filtered = playbooks.filter(pb => {
    const matchSearch = !search || pb.name?.toLowerCase().includes(search.toLowerCase()) || pb.description?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || pb.domain === filter;
    return matchSearch && matchFilter;
  });

  const domains = [...new Set(playbooks.map(pb => pb.domain).filter(Boolean))];

  // --- CRUD handlers ---
  const openCreateForm = () => {
    setEditingSlug(null);
    setForm({ ...EMPTY_FORM, voices: EMPTY_FORM.voices.map(v => ({ ...v })) });
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = (pb) => {
    setEditingSlug(pb.slug);
    const voices = Array.isArray(pb.voices) ? pb.voices : [];
    setForm({
      name: pb.name || '',
      description: pb.description || '',
      domain: pb.domain || '',
      category: pb.category || 'custom',
      voices: voices.length > 0 ? voices : [...EMPTY_FORM.voices],
      phases: Array.isArray(pb.phases) ? pb.phases : [],
      complexity_tier: pb.complexity_tier || 'standard',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editingSlug) {
        await API.put(`/api/user/playbooks/${editingSlug}`, form);
      } else {
        await API.post('/api/user/playbooks', form);
      }
      setShowForm(false);
      loadMyPlaybooks();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to save playbook');
    }
    setSaving(false);
  };

  const handleDelete = async (slug) => {
    try {
      await API.delete(`/api/user/playbooks/${slug}`);
      setDeleteConfirm(null);
      loadMyPlaybooks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleFork = async (slug) => {
    if (!user) return;
    setForking(slug);
    try {
      await API.post(`/api/user/playbooks/fork/${slug}`);
      // Switch to My Playbooks tab to show the fork
      setActiveTab('my');
      loadMyPlaybooks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to fork playbook');
    }
    setForking(null);
  };

  // --- Voice form helpers ---
  const addVoice = () => {
    if (form.voices.length >= 7) return;
    setForm(prev => ({
      ...prev,
      voices: [...prev.voices, {
        name: `Voice ${prev.voices.length + 1}`,
        mandate: '',
        model: 'google/gemini-2.5-flash',
        color: VOICE_COLORS[prev.voices.length % VOICE_COLORS.length],
      }],
    }));
  };

  const removeVoice = (idx) => {
    if (form.voices.length <= 2) return;
    setForm(prev => ({ ...prev, voices: prev.voices.filter((_, i) => i !== idx) }));
  };

  const updateVoice = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      voices: prev.voices.map((v, i) => i === idx ? { ...v, [field]: value } : v),
    }));
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="playbook-gallery-title">Playbook Library</h1>
          <p className="text-[#A0A0A0]">Pre-built templates for structured AI committee debates</p>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-0 mb-8 border-b border-[#2A2A2A]">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-5 py-3 min-h-[44px] text-sm font-mono transition-colors border-b-2 ${
              activeTab === 'browse'
                ? 'text-[#FF4D00] border-[#FF4D00]'
                : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
            }`}
            data-testid="tab-browse"
          >
            Browse
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-5 py-3 min-h-[44px] text-sm font-mono transition-colors border-b-2 ${
              activeTab === 'my'
                ? 'text-[#FF4D00] border-[#FF4D00]'
                : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
            }`}
            data-testid="tab-my-playbooks"
          >
            My Playbooks
            {myPlaybooks.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-[#FF4D00]/20 text-[#FF4D00] font-mono">{myPlaybooks.length}</span>
            )}
          </button>
        </div>

        {/* ===================== BROWSE TAB ===================== */}
        {activeTab === 'browse' && (
          <>
            <div className="flex flex-col sm:flex-wrap sm:flex-row gap-3 mb-8">
              <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
                <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text" value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Search playbooks..." data-testid="playbook-search"
                />
              </div>
              <div className="flex gap-2 flex-wrap overflow-x-auto">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-2 min-h-[44px] sm:min-h-0 text-xs font-mono border transition-colors ${filter === 'all' ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/40'}`}
                  data-testid="filter-all"
                >ALL</button>
                {domains.map(d => (
                  <button
                    key={d}
                    onClick={() => setFilter(d)}
                    className={`px-3 py-2 min-h-[44px] sm:min-h-0 text-xs font-mono border capitalize transition-colors ${filter === d ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/40'}`}
                    data-testid={`filter-${d}`}
                  >{d}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-48 animate-shimmer" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="playbook-grid">
                {/* User Defined Fray card */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Link
                    to="/fray/custom"
                    className="block p-6 bg-[#1A1A1A] border border-[#FF4D00]/30 hover:border-[#FF4D00] transition-all h-full group"
                    data-testid="playbook-card-custom"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-xs font-mono text-[#FF4D00]">CUSTOM</span>
                      <span className="text-[10px] px-2 py-0.5 border border-[#FF4D00]/30 text-[#FF4D00] font-mono">USER DEFINED</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 font-['Barlow_Condensed'] group-hover:text-[#FF4D00] transition-colors">User Defined Fray</h3>
                    <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">Build your own committee from scratch. Pick 2-7 Voices, define their roles and models, paste any prompt.</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#FF4D00] font-mono border-t border-[#2A2A2A] pt-4">
                      <Plus className="w-3 h-3" />
                      <span>2-7 Voices</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>2-5 Phases</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>Any Domain</span>
                    </div>
                  </Link>
                </motion.div>

                {/* Import Prompt card */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                  <Link
                    to="/fray/import"
                    className="block p-6 bg-[#1A1A1A] border border-[#A855F7]/30 hover:border-[#A855F7] transition-all h-full group"
                    data-testid="playbook-card-import"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-xs font-mono text-[#A855F7]">IMPORT</span>
                      <span className="text-[10px] px-2 py-0.5 border border-[#A855F7]/30 text-[#A855F7] font-mono">PROMPT PARSER</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 font-['Barlow_Condensed'] group-hover:text-[#A855F7] transition-colors">Import Prompt</h3>
                    <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">Paste an externally developed prompt. AI parses it to extract voices, mandates, phases, and models automatically.</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#A855F7] font-mono border-t border-[#2A2A2A] pt-4">
                      <Upload className="w-3 h-3" />
                      <span>Paste Markdown</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>Auto-Parse</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>Model Mapping</span>
                    </div>
                  </Link>
                </motion.div>

                {/* AI Prompt Builder card */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Link
                    to="/fray/builder"
                    className="block p-6 bg-[#1A1A1A] border border-[#22C55E]/30 hover:border-[#22C55E] transition-all h-full group"
                    data-testid="playbook-card-builder"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-xs font-mono text-[#22C55E]">AI GUIDED</span>
                      <span className="text-[10px] px-2 py-0.5 border border-[#22C55E]/30 text-[#22C55E] font-mono">CHAT</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 font-['Barlow_Condensed'] group-hover:text-[#22C55E] transition-colors">Prompt Builder</h3>
                    <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">Design your committee through conversation with AI. Describe your scenario and get an optimized debate spec.</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#22C55E] font-mono border-t border-[#2A2A2A] pt-4">
                      <Sparkles className="w-3 h-3" />
                      <span>AI Guided</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>Chat Interface</span>
                      <span className="text-[#2A2A2A]">|</span>
                      <span>One-Click Build</span>
                    </div>
                  </Link>
                </motion.div>

                {filtered.map((pb, i) => (
                  <motion.div key={pb.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                    <div className="relative h-full group">
                      <Link
                        to={`/playbooks/${pb.slug}`}
                        className="block p-6 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/40 transition-all h-full"
                        data-testid={`playbook-card-${pb.slug}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <span className="text-xs font-mono text-[#A0A0A0]">{pb.template_code}</span>
                          <span className={`text-[10px] px-2 py-0.5 border font-mono ${pb.revenue_tier === 'free' ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#F59E0B] border-[#F59E0B]/30'}`}>
                            {pb.revenue_tier === 'free' ? 'FREE' : 'PRO'}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold mb-2 font-['Barlow_Condensed'] group-hover:text-[#FF4D00] transition-colors">{pb.name}</h3>
                        <p className="text-[#A0A0A0] text-sm leading-relaxed mb-6">{pb.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {pb.template_config?.voices?.slice(0, 3).map((v, vi) => (
                            <span key={vi} className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{v.name}</span>
                          ))}
                          {(pb.template_config?.voices?.length || 0) > 3 && (
                            <span className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">+{pb.template_config.voices.length - 3}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-[10px] text-[#A0A0A0] font-mono border-t border-[#2A2A2A] pt-4">
                          <span>{pb.voice_count || 5} Voices</span>
                          <span>{pb.phases || 3} Phases</span>
                          <span className="capitalize">{pb.complexity_tier}</span>
                        </div>
                      </Link>
                      {/* Fork button overlay */}
                      {user && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFork(pb.slug); }}
                          disabled={forking === pb.slug}
                          className="absolute top-3 right-3 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center bg-[#0F0F0F]/80 border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/40 opacity-0 group-hover:opacity-100 transition-all"
                          title="Fork to My Playbooks"
                          data-testid={`fork-${pb.slug}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===================== MY PLAYBOOKS TAB ===================== */}
        {activeTab === 'my' && (
          <>
            {!user ? (
              <div className="text-center py-16 border border-[#2A2A2A]">
                <p className="text-[#A0A0A0] text-sm mb-4">Sign in to create and manage your playbooks.</p>
                <Link to="/login" className="px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium hover:bg-[#FF4D00]/90 transition-colors inline-block">
                  Sign In
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-[#A0A0A0]">{myPlaybooks.length} custom playbook{myPlaybooks.length !== 1 ? 's' : ''}</p>
                  <button
                    onClick={openCreateForm}
                    className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium hover:bg-[#FF4D00]/90 transition-colors"
                    data-testid="create-playbook-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Create Playbook
                  </button>
                </div>

                {myLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {[1, 2, 3].map(i => <div key={i} className="h-48 bg-[#1A1A1A] border border-[#2A2A2A] animate-pulse" />)}
                  </div>
                ) : myPlaybooks.length === 0 ? (
                  <div className="text-center py-16 border border-[#2A2A2A]">
                    <Plus className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
                    <p className="text-[#A0A0A0] text-sm mb-2">No custom playbooks yet.</p>
                    <p className="text-[#666] text-xs mb-6">Create one from scratch or fork a playbook from the Browse tab.</p>
                    <button
                      onClick={openCreateForm}
                      className="px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium hover:bg-[#FF4D00]/90 transition-colors"
                    >
                      Create Your First Playbook
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="my-playbook-grid">
                    {myPlaybooks.map((pb, i) => (
                      <motion.div key={pb.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                        <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/40 transition-all h-full">
                          <div className="flex items-start justify-between mb-4">
                            <span className="text-xs font-mono text-[#FF4D00]">
                              {pb.forked_from ? 'FORKED' : 'CUSTOM'}
                            </span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => openEditForm(pb)}
                                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#FF4D00] transition-colors"
                                title="Edit playbook"
                                data-testid={`edit-${pb.slug}`}
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(pb.slug)}
                                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#EF4444] transition-colors"
                                title="Delete playbook"
                                data-testid={`delete-${pb.slug}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold mb-2 font-['Barlow_Condensed']">{pb.name}</h3>
                          <p className="text-[#A0A0A0] text-sm leading-relaxed mb-4 line-clamp-3">{pb.description}</p>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {(Array.isArray(pb.voices) ? pb.voices : []).slice(0, 3).map((v, vi) => (
                              <span key={vi} className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{v.name || `Voice ${vi + 1}`}</span>
                            ))}
                            {(Array.isArray(pb.voices) ? pb.voices.length : 0) > 3 && (
                              <span className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">+{pb.voices.length - 3}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-[10px] text-[#A0A0A0] font-mono border-t border-[#2A2A2A] pt-4">
                            <span>{(Array.isArray(pb.voices) ? pb.voices.length : 0)} Voices</span>
                            <span className="capitalize">{pb.complexity_tier || 'standard'}</span>
                            {pb.forked_from && <span className="text-[#A855F7]">forked</span>}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ===================== CREATE / EDIT MODAL ===================== */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowForm(false)} />
            <motion.div
              className="relative w-full max-w-2xl bg-[#1A1A1A] border border-[#2A2A2A] p-4 md:p-6 max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-3 right-3 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="text-xl font-bold font-['Barlow_Condensed'] mb-6">
                {editingSlug ? 'Edit Playbook' : 'Create Playbook'}
              </h2>

              {formError && (
                <div className="mb-4 px-4 py-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">
                  {formError}
                </div>
              )}

              {/* Name */}
              <div className="mb-4">
                <label className="block text-[10px] font-mono text-[#FF4D00] mb-1.5">NAME</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] transition-colors"
                  placeholder="My Custom Playbook"
                  data-testid="form-name"
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-[10px] font-mono text-[#FF4D00] mb-1.5">DESCRIPTION</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full h-20 px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] resize-none focus:outline-none focus:border-[#FF4D00] transition-colors"
                  placeholder="Describe what this playbook is for..."
                  data-testid="form-description"
                />
              </div>

              {/* Domain */}
              <div className="mb-4">
                <label className="block text-[10px] font-mono text-[#FF4D00] mb-1.5">DOMAIN</label>
                <input
                  type="text"
                  value={form.domain}
                  onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] transition-colors"
                  placeholder="e.g., business, creative, technical"
                  data-testid="form-domain"
                />
              </div>

              {/* Voices */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[10px] font-mono text-[#FF4D00]">VOICES ({form.voices.length}/7)</label>
                  <button
                    onClick={addVoice}
                    disabled={form.voices.length >= 7}
                    className="flex items-center gap-1 px-3 py-1.5 min-h-[44px] sm:min-h-0 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/40 disabled:opacity-30 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Add Voice
                  </button>
                </div>
                <div className="space-y-3">
                  {form.voices.map((voice, idx) => (
                    <div key={idx} className="p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: voice.color || VOICE_COLORS[idx % VOICE_COLORS.length] }}
                        />
                        <input
                          type="text"
                          value={voice.name}
                          onChange={e => updateVoice(idx, 'name', e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-transparent border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] transition-colors"
                          placeholder="Voice name"
                        />
                        {form.voices.length > 2 && (
                          <button
                            onClick={() => removeVoice(idx)}
                            className="p-1.5 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-[#A0A0A0] hover:text-[#EF4444] transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={voice.mandate || ''}
                        onChange={e => updateVoice(idx, 'mandate', e.target.value)}
                        className="w-full px-3 py-1.5 bg-transparent border border-[#2A2A2A] text-xs text-[#A0A0A0] focus:outline-none focus:border-[#FF4D00] transition-colors"
                        placeholder="Mandate / role description"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-[#2A2A2A]">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium hover:bg-[#FF4D00]/90 disabled:opacity-50 transition-colors"
                  data-testid="form-save"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : (editingSlug ? 'Update Playbook' : 'Create Playbook')}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#FF4D00]/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== DELETE CONFIRMATION MODAL ===================== */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70" onClick={() => setDeleteConfirm(null)} />
            <motion.div
              className="relative w-full max-w-sm bg-[#1A1A1A] border border-[#2A2A2A] p-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <h3 className="text-lg font-bold font-['Barlow_Condensed'] mb-3">Delete Playbook?</h3>
              <p className="text-sm text-[#A0A0A0] mb-6">This action cannot be undone. The playbook and all its configuration will be permanently deleted.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 px-4 py-3 min-h-[44px] bg-[#EF4444] text-white text-sm font-medium hover:bg-[#EF4444]/90 transition-colors"
                  data-testid="confirm-delete"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
